<template>
    <div>
        <table-page
            v-show="!addingCompany"
            :client="client"
            :warnings="warnings"
            :changelogs="changelogs"
            :hideTabs="true"
        >
            <fast-table
                :recordLabel="recordLabel"
                :sections="sections"
                :headers="headers"
                :data="tableData"
                :activeRowId="currentCompanyId"
                @click="currentCompanyId = $event.id"
            >
                <template v-slot:table-actions>
                    <button class="btn btn-primary btn-collapse" id="addCompanyButton" @click="addCompany">
                        <i class="far fa-fw fa-plus"></i> <span class="btn-text">Add Company</span>
                    </button>
                </template>
            </fast-table>

        </table-page>

        <div v-if="addingCompany" class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">
                    <h3>Add Company</h3>
                    <p>Set up a company for this client</p>
                    <div class="row" v-for="(company, idx) in formData.companies">
                        <div class="col-12">
                            <div class="card mb-2">
                                <div class="card-header pr-1 d-flex align-items-center">
                                    <div class="col pl-0">
                                        <span class="btnlike mr-auto">
                                            <h4 class="mb-0">
                                                {{ client.name }} - Company {{ client.companies.length + idx + 1 }}
                                            </h4>
                                        </span>
                                    </div>
                                    <div v-if="formData.companies.length > 1" class="col-1 delete-col">
                                        <a href @click.prevent="deleteCompanyFormData(idx)">
                                            <i class="far fa-trash-alt"></i>
                                        </a>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <form>
                                        <form-input label="EIN" v-model="company.EIN" :errors="errors.EIN" type="text" :maxlength="10"/>
                                        <form-input label="Legal Name" v-model="company.name" :errors="errors.name" type="text" :maxlength="100"/>
                                        <form-input label="Friendly Name" v-model="company.known_as" :errors="errors.known_as" type="text" :maxlength="100"/>
                                        <form-input label="Doing Business As" v-model="company.dba" :errors="errors.dba" type="text" :maxlength="100"/>
                                        <form-input label="Address 1" v-model="company.address_1" :errors="errors.address_1" type="text" :maxlength="100"/>
                                        <form-input label="Address 2 (Optional)" v-model="company.address_2" :errors="errors.address_2" type="text" :maxlength="100"/>
                                        <form-input label="City" v-model="company.city" :errors="errors.city" type="text" :maxlength="100"/>

                                        <div class="form-row">
                                            <div class="col-6">
                                                <form-input label="State" v-model="company.state" :errors="errors.state" type="select" :options="stateOptions"/>
                                            </div>

                                            <div class="col-6">
                                                <form-input label="Zip Code" :maxlength="5" v-model="company.postal_code" :errors="errors.postal_code" type="text" inputmode="tel" />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row justify-content-center">
                        <a href class="btn btn-link mb-2" @click.prevent="addCompanyFormData()"><i class="far fa-plus mr-hf"></i>Add Another Company</a>
                    </div>

                    <form>
                        <form-errors :errors="formErrors"/>

                        <div class="row mb-2 d-flex align-items-center">
                            <div class="col">
                                <button class="btn-outline-primary btn" @click.prevent="addingCompany = false; formData = makeFormData()">Back</button>
                            </div>

                            <div class="col text-right">
                                <button class="btn btn-primary" @click.prevent="onSubmit">Save</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <company-sidebar
            v-if="currentCompany"
            :client="client"
            :company-id="currentCompany.id"
            ref="companySidebar"
            @updated="$emit('updated')"
            @close="currentCompanyId = null"
        >
        </company-sidebar>
    </div>
</template>

<script>
import TablePageMixin from '@/mixins/TablePageMixin'
import FastTable from '@/components/FastTable'
import STATES from '@/states'
import CompanySidebar from './components/Companies/CompanySidebar'

export default {
    components: {FastTable, CompanySidebar},
    mixins: [TablePageMixin],
    props: ['client'],
    computed: {
        currentCompany() {
            return this.client.companies.find(e => (e.id == this.currentCompanyId))
        },
        sections() {
            let sections = [
                {title: 'Active', defaultIsClosed: false, id: 'active', hasDateFilter: false},
                {title: 'Inactive', defaultIsClosed: true, id: 'inactive', hasDateFilter: true},
            ]
            return sections
        },
        // TODO KB CS add is_active to Company
        activeCompanies() {
            return this.client.companies
            // return this.client.companies.filter((c) => {return c.is_active})
        },
        inactiveCompanies() {
            return []
            // return this.client.companies.filter((c) => {return !c.is_active})
        },
        tableData() {
            const tableData = []
            const subsets = [
                this.activeCompanies,
                this.inactiveCompanies,
            ]

            subsets.forEach(companies => {
                const sectionData = []

                companies.forEach(company => {
                    sectionData.push({
                        id: company.id,
                        object: company,
                        cells: this.makeCompanyTableData(company),
                    })
                })
                tableData.push(sectionData)
            })
            return tableData
        },
        stateOptions() {
            const opts = []
            STATES.forEach(s => {
                opts.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return opts
        },
    },
    data() {
        return {
            addingCompany: false,
            currentCompanyId: null,
            recordLabel: "Companies",
            headers: [
                {label: 'Legal Name', classes: 'cell-company', isSearchable: true, defaultSort: true},
                {label: 'Warnings', classes: 'cw-warning', isFilterable: true},
                {label: 'Company #', classes: '', isSearchable: true},
                {label: 'EIN', classes: '', isSearchable: true},
            ],
            formData: this.makeFormData(),
            errors: [],
            formErrors: [],
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Companies')
    },
    methods: {
        makeCompanyTableData(company) {
            return [
                company.name,
                '', // warnings
                company.company_code,
                company.EIN,
            ]
        },
        addCompany() {
            this.addingCompany = true
        },
        makeCompanyFormData() {
            return {
                    EIN: '',
                    name: '',
                    known_as: '',
                    dba: '',
                    address_1: '',
                    address_2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                }
        },
        makeFormData() {
            return {
                companies: [this.makeCompanyFormData()]
            }
        },
        addCompanyFormData() {
            this.formData.companies.push(this.makeCompanyFormData())
        },
        deleteCompanyFormData(idx) {
            this.formData.companies.splice(idx, 1);
        },
        getFormData() {
            return this.formData
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/settings/companies`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.formData = this.makeFormData()
                this.$emit('updated')
            }).catch((errors) => {
                console.log(errors)
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    },
}
</script>
